/**
 * Owner-scoped device cache for the `['categories']` and `['preferences']` queries
 * (quick-261021-rcw).
 *
 * Why: the iOS "Add entry" quick action cold-starts the app straight into the add
 * form, whose category dropdown and Save button used to wait on GET /categories and
 * GET /preferences. Both answers change rarely, so the last server answer is kept in
 * localStorage and fed to TanStack Query as `initialData` (+ `initialDataUpdatedAt`,
 * so a stale seed still refetches on mount). The background refetch replaces it, and
 * structural sharing means the UI only re-renders when the server data differs.
 *
 * Scope (threat T-rcw-01): every stored copy is tagged with its OWNER — the Cognito
 * `sub` of the signed-in user, or `'local'` for iOS local mode. A read returns data
 * only when the stored owner equals the current owner, and any owner change (sign-out,
 * account switch, local <-> account) wipes the stored copy. The owner is set from the
 * `_app` route's beforeLoad (see routes/_app.tsx), which runs for every sign-in path,
 * including native Google sign-in (which emits no Hub `signedIn` event).
 *
 * Local mode never writes here: lib/localStore.ts is already a synchronous on-device
 * source, so the hooks seed from it directly.
 *
 * Multiple web tabs (review CR-01): `currentOwner` is per tab, but on web Amplify keeps
 * the Cognito tokens in shared localStorage, so another tab can sign in as someone else
 * without this tab's beforeLoad running. Two guards close that gap:
 * - An owner verifier (setOwnerVerifier — on web, a synchronous read of the sub in
 *   Amplify's token store) must agree with `currentOwner` before anything is read or
 *   written, so a stale tab never saves the new account's data under its old owner.
 * - A write never replaces a stored copy that belongs to another owner; only
 *   setCacheOwner wipes. lib/queryClient.ts also re-derives the owner from `storage`
 *   events (resyncCacheOwnerFromTokenStore) and drops that tab's in-memory queries.
 *
 * Deliberately pure TypeScript: no Amplify, zustand, React or DOM imports beyond
 * `globalThis.localStorage` (read inside try/catch), so node unit tests can import it
 * without side effects. Every storage access is wrapped — a failure means "no cache",
 * which simply falls back to today's network fetch (T-rcw-04).
 */
import type { QueryClient, Query } from '@tanstack/react-query';

/** The single storage key. Bump the `v1` suffix to bust every device's cache. */
export const DEVICE_CACHE_STORAGE_KEY = 'brokeornot.cache.v1';

export type CachedQueryName = 'categories' | 'preferences';

export interface CachedQuery<T> {
  data: T;
  savedAt: number;
}

interface StoredCache {
  owner: string;
  queries: Partial<Record<CachedQueryName, { savedAt: number; data: unknown }>>;
}

const CACHED_NAMES: readonly CachedQueryName[] = ['categories', 'preferences'];

let currentOwner: string | null = null;

/**
 * Returns the sub that the shared token store holds right now (null = no session), or
 * is null itself when there is no such shared store to check (native: one WebView).
 */
let ownerVerifier: (() => string | null) | null = null;

/**
 * True while the session is valid but its sub could not be read (review WR-04): reads and
 * writes are off, but nothing is wiped and `currentOwner` is kept, so a transient failure
 * does not drop every query and then drop them again once the sub is readable.
 */
let ownerUnknown = false;

/** The View-as target, as reported to attachDeviceCachePersistence (null = not impersonating). */
let impersonatedSubProbe: () => string | null = () => null;

/** Prefix of every key Amplify's default (web) token store writes to localStorage. */
export const COGNITO_STORAGE_KEY_PREFIX = 'CognitoIdentityServiceProvider.';

function storage(): Storage | undefined {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage;
  } catch {
    return undefined;
  }
}

function readStored(): StoredCache | undefined {
  try {
    const raw = storage()?.getItem(DEVICE_CACHE_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as StoredCache).owner !== 'string' ||
      !(parsed as StoredCache).queries ||
      typeof (parsed as StoredCache).queries !== 'object'
    ) {
      return undefined;
    }
    return parsed as StoredCache;
  } catch {
    return undefined;
  }
}

/** Removes the stored cache entirely. Safe to call at any time. */
export function clearDeviceQueryCache(): void {
  try {
    storage()?.removeItem(DEVICE_CACHE_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/** The owner the cache is currently scoped to (Cognito sub, 'local', or null). */
export function getCacheOwner(): string | null {
  return currentOwner;
}

/**
 * Scopes the cache to `owner`. If the stored copy belongs to anyone else (including
 * when `owner` is null), it is wiped. Returns true only when the owner actually changed.
 */
export function setCacheOwner(owner: string | null): boolean {
  const changed = owner !== currentOwner;
  currentOwner = owner;
  ownerUnknown = false;
  const stored = readStored();
  if (stored && stored.owner !== owner) clearDeviceQueryCache();
  return changed;
}

/**
 * Marks the owner as unknown (valid session, unreadable sub): no seed, no persist, no
 * wipe, and the current owner is kept. The next setCacheOwner call ends it.
 */
export function markCacheOwnerUnknown(): void {
  ownerUnknown = true;
}

/** Objects that must never be persisted, by identity (see excludeFromDeviceCache). */
const notPersistable = new WeakSet<object>();

/**
 * Marks `data` (by identity) as never to be persisted — e.g. a stand-in answer that is
 * not the account's real data (review IN-01: the /preferences 404 fallback). The query
 * must keep that identity in its cache (structuralSharing) for this to apply.
 */
export function excludeFromDeviceCache(data: object): void {
  notPersistable.add(data);
}

/** The cache owner captured when a mutation starts (see isCacheOwnerUnchanged). */
export interface CacheOwnerSnapshot {
  owner: string | null;
}

/** For a mutation's onMutate: remembers who owned the cache when the request started. */
export function captureCacheOwner(): CacheOwnerSnapshot {
  return { owner: currentOwner };
}

/**
 * For a mutation's onSuccess (review WR-05): false when the owner changed since
 * onMutate (sign-out / account switch mid-request), in which case the result belongs to
 * the previous owner and must not be written into the new owner's queries — the
 * persistence subscriber cannot tell, because setQueryData has no fetch start.
 */
export function isCacheOwnerUnchanged(snapshot: CacheOwnerSnapshot | undefined): boolean {
  return snapshot !== undefined && snapshot.owner === currentOwner;
}

/** Installs (or with null, removes) the owner verifier — see the header. */
export function setOwnerVerifier(verifier: (() => string | null) | null): void {
  ownerVerifier = verifier;
}

/** True when no verifier is installed, or the shared token store agrees on `owner`. */
function ownerConfirmed(owner: string): boolean {
  if (ownerVerifier === null) return true;
  try {
    return ownerVerifier() === owner;
  } catch {
    return false;
  }
}

/**
 * The Cognito sub in Amplify's default web token store (localStorage), read
 * synchronously the same way Amplify's DefaultTokenStore locates it:
 * `CognitoIdentityServiceProvider.<clientId>.LastAuthUser` names the user, whose
 * `….<user>.accessToken` JWT carries the sub. Null when signed out or unreadable.
 */
export function readCognitoSubFromStorage(clientId: string): string | null {
  try {
    const s = storage();
    if (!s) return null;
    const prefix = `${COGNITO_STORAGE_KEY_PREFIX}${clientId}`;
    const user = s.getItem(`${prefix}.LastAuthUser`) ?? 'username';
    const token = s.getItem(`${prefix}.${user}.accessToken`);
    const payload = token?.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const sub: unknown = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub ? sub : null;
  } catch {
    return null;
  }
}

/** True for a `storage` event key that can mean another tab changed auth or the cache. */
export function isAuthOrCacheStorageKey(key: string | null): boolean {
  return (
    key === null || key === DEVICE_CACHE_STORAGE_KEY || key.startsWith(COGNITO_STORAGE_KEY_PREFIX)
  );
}

/**
 * Re-derives the owner from the shared token store after another tab changed it. A tab
 * that is not scoped yet (owner null) is left for its next beforeLoad. Returns true when
 * the owner changed — the caller must then drop its in-memory queries.
 */
export function resyncCacheOwnerFromTokenStore(): boolean {
  if (ownerVerifier === null || currentOwner === null) return false;
  let sub: string | null;
  try {
    sub = ownerVerifier();
  } catch {
    sub = null;
  }
  if (sub === currentOwner) return false;
  return setCacheOwner(sub);
}

function isValidShape(name: CachedQueryName, data: unknown): boolean {
  if (name === 'categories') return Array.isArray(data);
  return (
    !!data &&
    typeof data === 'object' &&
    typeof (data as { currency?: unknown }).currency === 'string'
  );
}

/**
 * The current owner's cached copy of `name`, or undefined when there is none, the
 * owner is unknown/mismatched, the stored JSON is corrupt or the wrong shape, or an
 * admin is in "View as" (review WR-03: the stored copy is the admin's own, never the
 * target's, so it must not seed the target's queries).
 */
export function readCachedQuery<T>(name: CachedQueryName): CachedQuery<T> | undefined {
  if (currentOwner === null || ownerUnknown || !ownerConfirmed(currentOwner)) return undefined;
  if (impersonatedSubProbe() !== null) return undefined;
  const stored = readStored();
  if (!stored || stored.owner !== currentOwner) return undefined;
  const entry = stored.queries[name];
  if (!entry || typeof entry !== 'object' || typeof entry.savedAt !== 'number') return undefined;
  if (!isValidShape(name, entry.data)) return undefined;
  // Clamped (review IN-02): a copy saved while the clock ran fast must not count as fresh
  // (no mount refetch) until real time catches up.
  return { data: entry.data as T, savedAt: Math.min(entry.savedAt, Date.now()) };
}

/**
 * Persists `data` as the current owner's copy of `name`. No-op without an owner, in
 * local mode, and when the shared token store says someone else is signed in now. A
 * stored object that belongs to a different owner is left alone — it can only have been
 * written by another tab after this one was scoped, and only setCacheOwner may wipe.
 */
export function writeCachedQuery(name: CachedQueryName, data: unknown): void {
  const owner = currentOwner;
  if (owner === null || owner === 'local' || ownerUnknown) return;
  if (!isValidShape(name, data)) return;
  if (notPersistable.has(data as object)) return;
  if (!ownerConfirmed(owner)) return;
  const stored = readStored();
  if (stored && stored.owner !== owner) return;
  const entry = { savedAt: Date.now(), data };
  const next: StoredCache = { owner, queries: { ...stored?.queries, [name]: entry } };
  try {
    storage()?.setItem(DEVICE_CACHE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota exceeded / storage disabled — the cache is an optimisation only.
  }
}

function cachedNameOf(query: Query): CachedQueryName | undefined {
  const head = query.queryKey[0];
  return CACHED_NAMES.find((n) => n === head);
}

/**
 * Subscribes to `client`'s QueryCache and persists every successful
 * `['categories']` / `['preferences']` result (network fetches AND setQueryData from
 * mutations — both dispatch `action.type === 'success'`) for the current owner.
 *
 * Write guards (threat T-rcw-01 / T-rcw-02):
 * - `getImpersonatedSub()` is read when a fetch STARTS and again when it succeeds; if
 *   either is non-null (an admin "View as" target), nothing is persisted, so a target
 *   user's data can never be stored under the admin's owner — even when View-as is
 *   switched on or off while the request is in flight.
 * - The cache owner is captured at fetch start too; a result that lands after the owner
 *   changed (sign-out / account switch mid-request) is dropped.
 * - A query that has been removed from the cache (beforeLoad's removeQueries on an
 *   owner change) is never persisted.
 * `getImpersonatedSub` also gates readCachedQuery, so View-as never seeds from the cache.
 *
 * Returns the unsubscribe function.
 */
export function attachDeviceCachePersistence(
  client: QueryClient,
  getImpersonatedSub: () => string | null,
): () => void {
  const queryCache = client.getQueryCache();
  impersonatedSubProbe = getImpersonatedSub;
  const fetchStarts = new WeakMap<Query, { owner: string | null; impersonatedSub: string | null }>();

  const unsubscribe = queryCache.subscribe((event) => {
    if (event.type !== 'updated') return;
    const name = cachedNameOf(event.query);
    if (!name) return;
    const { action } = event;

    if (action.type === 'fetch') {
      fetchStarts.set(event.query, {
        owner: getCacheOwner(),
        impersonatedSub: getImpersonatedSub(),
      });
      return;
    }

    if (action.type === 'error') {
      fetchStarts.delete(event.query);
      return;
    }

    if (action.type !== 'success') return;

    const start = action.manual ? undefined : fetchStarts.get(event.query);
    if (!action.manual) fetchStarts.delete(event.query);

    if (getImpersonatedSub() !== null) return;
    if (start && (start.impersonatedSub !== null || start.owner !== getCacheOwner())) return;
    if (queryCache.get(event.query.queryHash) !== event.query) return;

    writeCachedQuery(name, event.query.state.data);
  });
  return () => {
    unsubscribe();
    if (impersonatedSubProbe === getImpersonatedSub) impersonatedSubProbe = () => null;
  };
}
